import type { SupabaseClient } from '@supabase/supabase-js';
import {
  ActivityInUseError,
  type ActivityCatalogStore,
  type BoardActivityUpsert,
  type ChannelCurationStore,
  type RegisterWriteStore,
  type ShiftDecision,
  type ShiftRequest,
  type ShiftRequestInput,
  type ShiftRequestStore,
  type TimesheetWriteStore,
  type TrustedPersonWriteStore,
  type DeskListStore,
  type StaffingConfigStore,
  type StaffingConfigSnapshot,
  type MetricStore,
  type StaffingProfileUpsert,
  type StaffingPostUpsert,
  type WorkContentUpsert,
} from '../ports.js';
import { METRIC_COLUMNS, metricUpsertToRow, type MetricRow, type MetricUpsert } from '../metricRows.js';
import type { DeskListEntry } from '../domain.js';

/**
 * The operator-only write adapters, with the BROWSER's authed client under RLS.
 *
 * They share a file because they share exactly one property — every one of them
 * is reachable only from the back-office — and nothing else. The ports stay
 * separate because the aggregates are: the activity catalogue, the time clock,
 * the personnel register and the AI curation queue have different invariants,
 * different tables and different reasons to change.
 */

/** Domain → columns. The one place `board_activities`' column names appear. */
function activityRow(input: BoardActivityUpsert): Record<string, unknown> {
  return {
    label: input.label,
    window_start: input.windowStart,
    window_end: input.windowEnd,
    untimed: input.untimed,
    window_note: input.windowNote,
    demand_driver: input.demandDriver,
    demand_formula: input.demandFormula,
    min_demand: input.minDemand,
    active: input.active,
    sort: input.sort,
    team: input.team,
    load_metric_key: input.loadMetricKey,
    badge_metric_key: input.badgeMetricKey,
  };
}

export class SupabaseActivityCatalogStore implements ActivityCatalogStore {
  constructor(private readonly db: SupabaseClient) {}

  async createActivity(input: BoardActivityUpsert): Promise<void> {
    const { error } = await this.db.from('board_activities').insert(activityRow(input));
    if (error) throw new Error(`board_activities.insert: ${error.message}`);
  }

  async updateActivity(id: number, input: BoardActivityUpsert): Promise<void> {
    const { error } = await this.db.from('board_activities').update(activityRow(input)).eq('id', id);
    if (error) throw new Error(`board_activities.update: ${error.message}`);
  }

  async deleteActivity(id: number, label: string): Promise<void> {
    const { error } = await this.db.from('board_activities').delete().eq('id', id);
    if (!error) return;
    // 23503 = FK RESTRICT (migration 0048). Translated HERE, so the view never
    // has to know a PostgREST error code to state a business rule.
    if (error.code === '23503') throw new ActivityInUseError(label);
    throw new Error(`board_activities.delete: ${error.message}`);
  }

  async addQualification(activityId: number, employeeId: string): Promise<void> {
    const { error } = await this.db
      .from('board_activity_qualifications')
      .insert({ activity_id: activityId, employee_id: employeeId });
    if (error) throw new Error(`board_activity_qualifications.insert: ${error.message}`);
  }

  async removeQualification(activityId: number, employeeId: string): Promise<void> {
    const { error } = await this.db
      .from('board_activity_qualifications')
      .delete()
      .eq('activity_id', activityId)
      .eq('employee_id', employeeId);
    if (error) throw new Error(`board_activity_qualifications.delete: ${error.message}`);
  }

  async setMainActivity(activityId: number, employeeId: string, isMain: boolean): Promise<void> {
    // UPDATE, never upsert: « principale » qualifies an existing qualification.
    // Creating the row here would let somebody's main activity be one they are
    // not qualified for, which has no meaning.
    const { error } = await this.db
      .from('board_activity_qualifications')
      .update({ is_main: isMain })
      .eq('activity_id', activityId)
      .eq('employee_id', employeeId);
    if (error) throw new Error(`board_activity_qualifications.is_main: ${error.message}`);
  }
}

export class SupabaseTimesheetWriteStore implements TimesheetWriteStore {
  constructor(private readonly db: SupabaseClient) {}

  async updateEntry(id: number, clockInIso: string, clockOutIso: string | null): Promise<void> {
    // The 0018 trigger stamps updated_by/updated_at — the correction trace is
    // the database's job, not a field the caller supplies.
    const { error } = await this.db
      .from('staff_time_entries')
      .update({ clock_in: clockInIso, clock_out: clockOutIso })
      .eq('id', id);
    if (error) throw new Error(`staff_time_entries.update: ${error.message}`);
  }

  async deleteEntry(id: number): Promise<void> {
    const { error } = await this.db.from('staff_time_entries').delete().eq('id', id);
    if (error) throw new Error(`staff_time_entries.delete: ${error.message}`);
  }

  async addEntry(employeeId: string, clockInIso: string, clockOutIso: string | null): Promise<void> {
    const { error } = await this.db
      .from('staff_time_entries')
      .insert({ employee_id: employeeId, clock_in: clockInIso, clock_out: clockOutIso });
    if (error) throw new Error(`staff_time_entries.insert: ${error.message}`);
  }
}

/** « Hors Staff » writes (ADR-0024) — RPCs only; RLS closes the table to trusted rows. */
interface DeskListRow {
  id: string;
  first_name: string;
  last_name: string;
  nickname: string | null;
  kind: 'employee' | 'trusted';
  has_pin: boolean;
  desk_operator: boolean;
  archived: boolean;
}

/** The desk list (ADR-0026): the roster RPC and O1's toggle. */
export class SupabaseDeskListStore implements DeskListStore {
  constructor(private readonly db: SupabaseClient) {}

  async roster(): Promise<DeskListEntry[]> {
    const { data, error } = await this.db.rpc('staff_desk_operator_roster');
    if (error) throw new Error(`staff_desk_operator_roster: ${error.message}`);
    return ((data ?? []) as DeskListRow[]).map((r) => ({
      id: r.id,
      firstName: r.first_name,
      lastName: r.last_name,
      nickname: r.nickname ?? null,
      kind: r.kind,
      hasPin: r.has_pin,
      listed: r.desk_operator,
      archived: r.archived,
    }));
  }

  async setListed(id: string, listed: boolean): Promise<void> {
    const { error } = await this.db.rpc('staff_set_desk_operator', { p_employee: id, p_on: listed });
    if (error) throw new Error(`staff_set_desk_operator: ${error.message}`);
  }
}

export class SupabaseTrustedPersonWriteStore implements TrustedPersonWriteStore {
  constructor(private readonly db: SupabaseClient) {}

  async create(firstName: string, lastName: string, nickname: string | null): Promise<string> {
    const { data, error } = await this.db.rpc('staff_admin_create_trusted', {
      p_first_name: firstName,
      p_last_name: lastName,
      p_nickname: nickname,
    });
    if (error) throw new Error(`staff_admin_create_trusted: ${error.message}`);
    return data as string;
  }

  async archive(id: string, archived: boolean): Promise<void> {
    const { error } = await this.db.rpc('staff_admin_archive_trusted', { p_id: id, p_archived: archived });
    if (error) throw new Error(`staff_admin_archive_trusted: ${error.message}`);
  }

  async setPin(id: string, pin: string): Promise<void> {
    // The same RPC as for employees: it updates any staff_employees row.
    const { error } = await this.db.rpc('staff_set_pin', { emp: id, pin });
    if (error) throw new Error(`staff_set_pin: ${error.message}`);
  }

  async setEntry(
    entryId: number | null,
    personId: string,
    clockInIso: string,
    clockOutIso: string | null,
  ): Promise<void> {
    const { error } = await this.db.rpc('staff_trusted_set_entry', {
      p_id: entryId,
      p_employee: personId,
      p_clock_in: clockInIso,
      p_clock_out: clockOutIso,
    });
    if (error) throw new Error(`staff_trusted_set_entry: ${error.message}`);
  }

  async deleteEntry(entryId: number): Promise<void> {
    const { error } = await this.db.rpc('staff_trusted_delete_entry', { p_id: entryId });
    if (error) throw new Error(`staff_trusted_delete_entry: ${error.message}`);
  }

  async setRetentionDays(days: number): Promise<void> {
    const { error } = await this.db.rpc('staff_admin_set_trusted_retention', { p_days: days });
    if (error) throw new Error(`staff_admin_set_trusted_retention: ${error.message}`);
  }
}

export class SupabaseRegisterWriteStore implements RegisterWriteStore {
  constructor(private readonly db: SupabaseClient) {}

  async upsertPerson(params: Record<string, unknown>): Promise<string> {
    const { data, error } = await this.db.rpc('staff_admin_upsert_person', params);
    if (error) throw new Error(`staff_admin_upsert_person: ${error.message}`);
    return data as string;
  }

  async createHire(
    personParams: Record<string, unknown>,
    contractParams: Record<string, unknown>,
  ): Promise<string> {
    const { data, error } = await this.db.rpc('staff_admin_create_hire', {
      p_person: personParams,
      p_contract: contractParams,
    });
    if (error) throw new Error(`staff_admin_create_hire: ${error.message}`);
    return data as string;
  }

  async upsertContract(params: Record<string, unknown>): Promise<void> {
    const { error } = await this.db.rpc('staff_admin_upsert_contract', params);
    if (error) throw new Error(`staff_admin_upsert_contract: ${error.message}`);
  }

  async updateContract(params: Record<string, unknown>): Promise<void> {
    const { error } = await this.db.rpc('staff_admin_update_contract', params);
    if (error) throw new Error(`staff_admin_update_contract: ${error.message}`);
  }

  async closeContract(params: Record<string, unknown>): Promise<void> {
    const { error } = await this.db.rpc('staff_admin_close_contract', params);
    if (error) throw new Error(`staff_admin_close_contract: ${error.message}`);
  }

  async deleteContract(contractId: string): Promise<void> {
    const { error } = await this.db.rpc('staff_admin_delete_contract', { p_id: contractId });
    if (error) throw new Error(`staff_admin_delete_contract: ${error.message}`);
  }

  async setPin(employeeId: string, pin: string): Promise<void> {
    // The RPC hashes server-side; the plaintext never reaches a column.
    const { error } = await this.db.rpc('staff_set_pin', { emp: employeeId, pin });
    if (error) throw new Error(`staff_set_pin: ${error.message}`);
  }

  async revokeAccess(employeeId: string, reason: string | null): Promise<void> {
    const { error } = await this.db.rpc('staff_admin_revoke_access', {
      p_employee: employeeId,
      p_reason: reason,
    });
    if (error) throw new Error(rpcMessage(error.message));
  }
}

/**
 * The offboarding and shift-request RPCs raise operator-readable French (« ces
 * heures chevauchent un autre pointage »). Strip the `rpc_name: ` prefix so the
 * view can show it as written — the prefix is for logs, not for the manager.
 */
function rpcMessage(message: string): string {
  return message.replace(/^staff_[a-z_]+:\s*/, '');
}

const SHIFT_REQUEST_COLUMNS =
  'id,employee_id,kind,target_entry_id,orig_clock_in,orig_clock_out,req_clock_in,req_clock_out,reason,status,decided_at,decision_note,final_clock_in,final_clock_out,created_at';

interface ShiftRequestRow {
  id: number;
  employee_id: string;
  kind: ShiftRequest['kind'];
  target_entry_id: number | null;
  orig_clock_in: string | null;
  orig_clock_out: string | null;
  req_clock_in: string;
  req_clock_out: string;
  reason: string | null;
  status: ShiftRequest['status'];
  decided_at: string | null;
  decision_note: string | null;
  final_clock_in: string | null;
  final_clock_out: string | null;
  created_at: string;
}

function rowToShiftRequest(r: ShiftRequestRow): ShiftRequest {
  return {
    id: r.id,
    employeeId: r.employee_id,
    kind: r.kind,
    targetEntryId: r.target_entry_id,
    origClockIn: r.orig_clock_in,
    origClockOut: r.orig_clock_out,
    reqClockIn: r.req_clock_in,
    reqClockOut: r.req_clock_out,
    reason: r.reason,
    status: r.status,
    decidedAt: r.decided_at,
    decisionNote: r.decision_note,
    finalClockIn: r.final_clock_in,
    finalClockOut: r.final_clock_out,
    createdAt: r.created_at,
  };
}

/**
 * Shift requests over the caller's authed client. Reads go through RLS (an
 * employee sees their own, the operator all); every write is an RPC, because the
 * table grants no INSERT/UPDATE — the accept path writes a time entry AND closes
 * the request, which only one transaction can do.
 */
export class SupabaseShiftRequestStore implements ShiftRequestStore {
  constructor(private readonly db: SupabaseClient) {}

  async send(input: ShiftRequestInput): Promise<number> {
    const { data, error } = await this.db.rpc('staff_request_shift', {
      p_kind: input.kind,
      p_target: input.targetEntryId,
      p_in: input.clockInIso,
      p_out: input.clockOutIso,
      p_reason: input.reason,
    });
    if (error) throw new Error(rpcMessage(error.message));
    return data as number;
  }

  async cancel(id: number): Promise<void> {
    const { error } = await this.db.rpc('staff_cancel_shift_request', { p_id: id });
    if (error) throw new Error(rpcMessage(error.message));
  }

  async listForEmployee(employeeId: string): Promise<ShiftRequest[]> {
    const { data, error } = await this.db
      .from('staff_shift_requests')
      .select(SHIFT_REQUEST_COLUMNS)
      .eq('employee_id', employeeId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) throw new Error(`staff_shift_requests.select: ${error.message}`);
    return ((data ?? []) as ShiftRequestRow[]).map(rowToShiftRequest);
  }

  async listPending(): Promise<ShiftRequest[]> {
    const { data, error } = await this.db
      .from('staff_shift_requests')
      .select(SHIFT_REQUEST_COLUMNS)
      .eq('status', 'pending')
      .order('created_at', { ascending: true });
    if (error) throw new Error(`staff_shift_requests.select: ${error.message}`);
    return ((data ?? []) as ShiftRequestRow[]).map(rowToShiftRequest);
  }

  async listDecided(limit: number): Promise<ShiftRequest[]> {
    const { data, error } = await this.db
      .from('staff_shift_requests')
      .select(SHIFT_REQUEST_COLUMNS)
      .neq('status', 'pending')
      .order('decided_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(`staff_shift_requests.select: ${error.message}`);
    return ((data ?? []) as ShiftRequestRow[]).map(rowToShiftRequest);
  }

  async decide(id: number, decision: ShiftDecision, note: string | null): Promise<void> {
    const { error } = await this.db.rpc('staff_decide_shift_request', {
      p_id: id,
      p_decision: decision.kind,
      p_in: decision.kind === 'modify' ? decision.clockInIso : null,
      p_out: decision.kind === 'modify' ? decision.clockOutIso : null,
      p_note: note,
    });
    if (error) throw new Error(rpcMessage(error.message));
  }
}

export class SupabaseChannelCurationStore implements ChannelCurationStore {
  constructor(private readonly db: SupabaseClient) {}

  async acceptCandidate(candidateId: string, finalBodyFr: string | null): Promise<void> {
    const { error } = await this.db.rpc('staff_candidate_accept', {
      candidate_id: candidateId,
      final_body_fr: finalBodyFr,
    });
    if (error) throw new Error(`staff_candidate_accept: ${error.message}`);
  }

  async dismissCandidate(candidateId: string): Promise<void> {
    const { error } = await this.db.rpc('staff_candidate_dismiss', { candidate_id: candidateId });
    if (error) throw new Error(`staff_candidate_dismiss: ${error.message}`);
  }
}

// ── Staffing configuration (predictive-staffing brief §4.5) ─────────────────
//
// Five tables, all `rh.board.manage` for writes and `rh.board.read` for reads
// (migration 20260729120100). Direct PostgREST under the operator session, same
// shape as the activity catalogue above.

export class SupabaseStaffingConfigStore implements StaffingConfigStore {
  constructor(private readonly db: SupabaseClient) {}

  async loadConfig(): Promise<StaffingConfigSnapshot> {
    // One round trip per table rather than a nested select: PostgREST embedding
    // would return a tree, and the pure assembler (staffingConfigFromRows)
    // takes flat lists — matching its input here keeps the adapter dumb.
    const [profiles, bands, posts, workContent, combinations] = await Promise.all([
      this.db.from('board_staffing_profile').select('id,label,archetype,demand_formula,demand_metric_key,active,sort,note'),
      this.db.from('board_staffing_band').select('id,profile_id,from_demand'),
      this.db.from('board_staffing_post').select('id,band_id,activity_id,label,window_start,window_end,heads,sort'),
      this.db
        .from('board_staffing_work_content')
        .select(
          'profile_id,minutes_a_blanc,minutes_recouche,minutes_recouche_fond,minutes_arrivee,comfort_floor_equivalents,deep_clean_cadence,earliest_start,default_start,latest_end,heavy_arrivals,check_in_early,check_in_normal,max_heads,target_finish',
        ),
      this.db.from('board_activity_combination').select('activity_a,activity_b'),
    ]);
    for (const [name, result] of [
      ['board_staffing_profile', profiles],
      ['board_staffing_band', bands],
      ['board_staffing_post', posts],
      ['board_staffing_work_content', workContent],
      ['board_activity_combination', combinations],
    ] as const) {
      if (result.error) throw new Error(`${name}.select: ${result.error.message}`);
    }
    return {
      profiles: (profiles.data ?? []) as StaffingConfigSnapshot['profiles'],
      bands: (bands.data ?? []) as StaffingConfigSnapshot['bands'],
      posts: (posts.data ?? []) as StaffingConfigSnapshot['posts'],
      workContent: (workContent.data ?? []) as StaffingConfigSnapshot['workContent'],
      combinations: (combinations.data ?? []) as StaffingConfigSnapshot['combinations'],
    };
  }

  async saveProfile(id: number | null, input: StaffingProfileUpsert): Promise<number> {
    const row = {
      label: input.label,
      archetype: input.archetype,
      demand_formula: input.demandFormula,
      active: input.active,
      sort: input.sort,
      note: input.note,
      updated_at: new Date().toISOString(),
    };
    // `select()` so the insert returns the new id: creating a profile is always
    // followed by creating its band, which cannot be addressed without it.
    const { data, error } = id === null
      ? await this.db.from('board_staffing_profile').insert(row).select('id').single()
      : await this.db.from('board_staffing_profile').update(row).eq('id', id).select('id').single();
    if (error) throw new Error(`board_staffing_profile.${id === null ? 'insert' : 'update'}: ${error.message}`);
    return (data as { id: number }).id;
  }

  async deleteProfile(id: number): Promise<void> {
    // Bands, posts and the work-content row cascade (ON DELETE CASCADE), so one
    // statement removes the whole profile without leaving orphans.
    const { error } = await this.db.from('board_staffing_profile').delete().eq('id', id);
    if (error) throw new Error(`board_staffing_profile.delete: ${error.message}`);
  }

  async saveWorkContent(profileId: number, input: WorkContentUpsert): Promise<void> {
    const { error } = await this.db.from('board_staffing_work_content').upsert(
      {
        profile_id: profileId,
        minutes_a_blanc: input.minutesABlanc,
        minutes_recouche: input.minutesRecouche,
        minutes_recouche_fond: input.minutesRecoucheFond,
        minutes_arrivee: input.minutesArrivee,
        comfort_floor_equivalents: input.comfortFloorEquivalents,
        deep_clean_cadence: input.deepCleanCadence,
        earliest_start: input.earliestStart,
        default_start: input.defaultStart,
        latest_end: input.latestEnd,
        heavy_arrivals: input.heavyArrivals,
        check_in_early: input.checkInEarly,
        check_in_normal: input.checkInNormal,
        max_heads: input.maxHeads,
        // Null, never '' — an empty string would not parse as a `time` and the
        // upsert would fail on the one field whose whole meaning is « unset ».
        target_finish: input.targetFinish === '' ? null : input.targetFinish,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'profile_id' },
    );
    if (error) throw new Error(`board_staffing_work_content.upsert: ${error.message}`);
  }

  async saveBand(id: number | null, profileId: number, fromDemand: number): Promise<number> {
    const { data, error } = id === null
      ? await this.db.from('board_staffing_band').insert({ profile_id: profileId, from_demand: fromDemand }).select('id').single()
      : await this.db.from('board_staffing_band').update({ from_demand: fromDemand }).eq('id', id).select('id').single();
    if (error) throw new Error(`board_staffing_band.${id === null ? 'insert' : 'update'}: ${error.message}`);
    return (data as { id: number }).id;
  }

  async deleteBand(id: number): Promise<void> {
    const { error } = await this.db.from('board_staffing_band').delete().eq('id', id);
    if (error) throw new Error(`board_staffing_band.delete: ${error.message}`);
  }

  async savePost(id: number | null, bandId: number, input: StaffingPostUpsert): Promise<void> {
    const row = {
      band_id: bandId,
      activity_id: input.activityId,
      label: input.label,
      window_start: input.windowStart,
      window_end: input.windowEnd,
      heads: input.heads,
      sort: input.sort,
    };
    const { error } = id === null
      ? await this.db.from('board_staffing_post').insert(row)
      : await this.db.from('board_staffing_post').update(row).eq('id', id);
    if (error) throw new Error(`board_staffing_post.${id === null ? 'insert' : 'update'}: ${error.message}`);
  }

  async deletePost(id: number): Promise<void> {
    const { error } = await this.db.from('board_staffing_post').delete().eq('id', id);
    if (error) throw new Error(`board_staffing_post.delete: ${error.message}`);
  }

  async addCombination(activityA: number, activityB: number): Promise<void> {
    const { error } = await this.db
      .from('board_activity_combination')
      .insert({ activity_a: activityA, activity_b: activityB });
    if (error) throw new Error(`board_activity_combination.insert: ${error.message}`);
  }

  async removeCombination(activityA: number, activityB: number): Promise<void> {
    const { error } = await this.db
      .from('board_activity_combination')
      .delete()
      .eq('activity_a', activityA)
      .eq('activity_b', activityB);
    if (error) throw new Error(`board_activity_combination.delete: ${error.message}`);
  }
}

// ── The metric registry (ADR-0015) ──────────────────────────────────────────
//
// `rh.board.read` to read, `rh.board.manage` to write — the same gates as the
// staffing tables it sits beside (migration 20260809100100).

export class SupabaseMetricStore implements MetricStore {
  constructor(private readonly db: SupabaseClient) {}

  async load(): Promise<MetricRow[]> {
    const { data, error } = await this.db.from('board_metric').select(METRIC_COLUMNS).order('sort');
    if (error) throw new Error(`board_metric.select: ${error.message}`);
    return (data ?? []) as MetricRow[];
  }

  async save(input: MetricUpsert): Promise<void> {
    // `key` is the primary key AND the identity, so one upsert covers create and
    // edit. The DB's validate trigger may still reject this (unknown reference,
    // cycle) — the message is propagated verbatim because it names exactly what
    // is wrong, which no generic client-side wording could.
    const { error } = await this.db
      .from('board_metric')
      .upsert(metricUpsertToRow(input), { onConflict: 'key' });
    if (error) throw new Error(error.message);
  }

  async remove(key: string): Promise<void> {
    const { error } = await this.db.from('board_metric').delete().eq('key', key);
    // The delete guard names the dependent formulas; surfacing that verbatim is
    // the whole value of the message.
    if (error) throw new Error(error.message);
  }
}
